import React, { useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Collapse,
  Select,
  MenuItem,
  Stack,
  styled,
  TableContainer,
} from "@mui/material";
import FAQAccordion from "../FAQAccordian.js";
import GoogleFAQs from "./GoogleFAQs.js";
import AppleFAQs from "./AppleFAQs.js";
import MicrosoftFAQs from "./MicrosoftFAQs.js";
import { useTheme } from "@emotion/react";

const StyledTableContainerShadow = styled(TableContainer)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: theme.shadows.cardLg(theme.palette, theme.palette.mode),
  border: "none",
  backgroundColor: theme.palette.background.paper,
  width: "100%",
  maxWidth: "100%",
}));

const TroubleshootingSection = ({
  show,
  provider,
  onProviderChange,
  onCopyLink,
  calendar,
}) => {
  const theme = useTheme();
  const providerOptions = [
    { value: "google", label: "Google" },
    { value: "apple", label: "Apple" },
    { value: "microsoft", label: "Outlook" },
  ];

  const getProviderContent = useCallback(
    (provider) => {
      switch (provider?.toLowerCase()) {
        case "google":
          return GoogleFAQs({ onCopyLink });

        case "apple":
          return AppleFAQs({ onCopyLink, calendar });

        case "microsoft":
          return MicrosoftFAQs({ onCopyLink });

        default:
          return [
            {
              question: "General troubleshooting",
              answer:
                "Try copying the calendar link and manually adding it to your calendar app's subscription or import feature.",
            },
          ];
      }
    },
    [onCopyLink, calendar]
  );

  const content = useMemo(
    () => getProviderContent(provider),
    [getProviderContent, provider]
  );

  return (
    <Collapse in={show}>
      <Box sx={{ mt: 4, width: "90%" }}>
        <StyledTableContainerShadow sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h2">Troubleshooting</Typography>
            <Select
              variant="outlined"
              value={provider}
              onChange={(e) => onProviderChange(e.target.value)}
              sx={{
                "& .MuiTypography-root": {
                  textDecorationLine: "underline",
                  textDecorationStyle: "dotted",
                  textDecorationColor: "text.secondary",
                  textDecorationThickness: "1px",
                  textUnderlineOffset: "6px",
                },
                ".MuiOutlinedInput-notchedOutline": {
                  display: "none",
                },
                ".MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 32px 8px 0",
                },
                ".MuiSelect-select .MuiSvgIcon-root": {
                  display: "none",
                },
              }}
              MenuProps={{
                sx: {
                  "li>.MuiTypography-h2": {
                    fontSize: `${theme.typography.h5.fontSize} !important`,
                    fontWeight: 500,
                  },
                },
              }}
            >
              {providerOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  translate="no" /* don't translate company names */
                >
                  <Typography variant="h2" translate="no">
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <FAQAccordion
            faqData={content}
            keyPrefix={provider?.toLowerCase() || "unknown"}
          />
        </StyledTableContainerShadow>
      </Box>
    </Collapse>
  );
};

export default TroubleshootingSection;
